// src/components/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { hasRouteAccess } from '../constants/roleAccess';
import { requiresProductChoice, hasProductAccess, PRODUCTS } from '../constants/productAccess';

// Account-level pages that make sense regardless of which product a user
// has - a City-Explorer-only user still needs to be able to change their
// password, for example.
const PRODUCT_AGNOSTIC_PATHS = ['/preferences', '/change-password'];

// Changed to function declaration and explicit export
export default function ProtectedRoute({ children, allowedRoles }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const currentPath = location.pathname;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Users with more than one explicit product grant (School Explorer + City
  // Explorer) pick one before landing in either product. Users with only
  // School Explorer (the vast majority, including all existing accounts)
  // are unaffected.
  if (currentPath !== '/select-tool' && requiresProductChoice(user)) {
    return <Navigate to="/select-tool" replace />;
  }

  // The tool picker itself isn't part of the role-based route whitelist below
  // (it's not a School Explorer page) - any authenticated user routed here
  // by the check above is allowed to see it.
  if (currentPath === '/select-tool') {
    return children;
  }

  // Everything ProtectedRoute guards is a School Explorer page. A user
  // provisioned with City Explorer only (no `school_explorer` in their
  // explicit `products`) gets sent to their own product instead. Every
  // existing account defaults to `products: ['school_explorer']`, so this
  // is a no-op for them.
  if (!PRODUCT_AGNOSTIC_PATHS.includes(currentPath) && !hasProductAccess(user, PRODUCTS.SCHOOL_EXPLORER)) {
    return <Navigate to="/city-explorer" replace />;
  }

  // Check role from enhanced user object
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check route access using role from enhanced user object
  if (!hasRouteAccess(user.role, currentPath)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}